import {
  Locales,
  getDatePlusNHours,
  Utils,
  TicketingFormCategory,
  MembershipValidityPeriod,
  ShopVariantType,
} from "@simplyk/common";
import { v4 } from "uuid";

import { EditTicketingRatesInput, RateOutput, RateFieldOutput, TicketingOutput } from "@/common/types/trpc";
import {
  RateData,
  RatePhotoData,
  ShopVariantData,
  VariantSeatData,
  // eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
} from "@/features/LiveFormEditor/Steps/StepTickets/Rates/types";

export const mapScheduleToApiFields = (
  rate: Pick<
    RateData,
    "isScheduled" | "salesStartMode" | "salesStartDate" | "salesStartAfterRateId" | "salesEndMode" | "salesEndDate"
  >
): {
  earlyBirdCloseDate: Date | null;
  salesStartDate: Date | null;
  salesStartAfterRateId: string | null;
  isSalesScheduled: boolean;
} => ({
  earlyBirdCloseDate: rate.isScheduled && rate.salesEndMode === "customDate" ? rate.salesEndDate : null,
  salesStartDate: rate.isScheduled && rate.salesStartMode === "customDate" ? rate.salesStartDate : null,
  salesStartAfterRateId: rate.isScheduled && rate.salesStartMode === "afterRate" ? rate.salesStartAfterRateId : null,
  isSalesScheduled: rate.isScheduled,
});

export const mapApiToScheduleFields = (rate: {
  earlyBirdCloseDate?: Date | string | null;
  salesStartDate?: Date | string | null;
  salesStartAfterRateId?: string | null;
  isSalesScheduled?: boolean | null;
}): Pick<
  RateData,
  "isScheduled" | "salesStartMode" | "salesStartDate" | "salesStartAfterRateId" | "salesEndMode" | "salesEndDate"
> => ({
  isScheduled: Boolean(
    rate.isSalesScheduled || rate.salesStartDate || rate.salesStartAfterRateId || rate.earlyBirdCloseDate
  ),
  salesStartMode: rate.salesStartDate ? "customDate" : rate.salesStartAfterRateId ? "afterRate" : "formDefault",
  salesStartDate: rate.salesStartDate ? new Date(rate.salesStartDate) : null,
  salesStartAfterRateId: rate.salesStartAfterRateId ?? null,
  salesEndMode: rate.earlyBirdCloseDate ? "customDate" : "formDefault",
  salesEndDate: rate.earlyBirdCloseDate ? new Date(rate.earlyBirdCloseDate) : null,
});

export const MINIMUM_BID_INCREMENT = 500;

export const mapDataToEditRates = (
  rates: RateData[],
  data: TicketingOutput | undefined
): EditTicketingRatesInput["rates"] => {
  if (!rates || rates.length === 0) {
    return [];
  }

  return rates.map((rate, index) => {
    const registeredRate = data?.rates?.find((rateToFind) => rateToFind.id === rate.id);
    const rateField = registeredRate?.rateFields?.find((rateField) => rateField.locale === data?.locale);
    const eligibleAmount = getEligibleAmountFromRate(rate);
    const isMembershipV2 = data?.formCategory === TicketingFormCategory.MembershipV2;

    const imagesUploaderData = rate.photos;
    const photosData: string[] | undefined = imagesUploaderData
      ?.map((imagesUploaderData) => {
        return imagesUploaderData.photoUrl;
      })
      .filter(Utils.notEmpty);

    return {
      id: rate.id,
      amount: rate.amount,
      groupMembershipMaxMembers: rate.isGroupMembership ? Number(rate.groupMembershipMaxMembers) : null,
      attendeesByTicket: rate.isGroup && rate.attendeesByTicket ? parseInt(rate.attendeesByTicket.toString()) : null,
      eligibleAmount,
      seats: parseValueToEditInput(rate.seats),
      minimumToBuy: parseValueToEditInput(rate.rateMinimumNumberToBuy),
      maximumToBuy: rate.rateMaximumNumberToBuy
        ? parseInt(rate.rateMaximumNumberToBuy.toString())
        : isMembershipV2
          ? 1
          : 10,
      membershipValidityPeriod: rate.membershipValidityPeriod as MembershipValidityPeriod,
      membershipValidityPeriodAtDate:
        (rate.membershipValidityPeriod === MembershipValidityPeriod.AtDate &&
          (rate.membershipValidityPeriodAtDate as Date)) ||
        null,
      rateFields: [
        {
          id: rateField?.id,
          title: rate.name,
          description: rate.description,
        },
      ],
      sortIndex: index,
      photos: photosData,
      ...mapScheduleToApiFields(rate),
      bidIncrement: rate.bidIncrement || MINIMUM_BID_INCREMENT,
      allowAutomaticRenewal: Boolean(rate.allowAutomaticRenewal),
    };
  });
};

const parseValueToEditInput = (value: number | null | undefined) => {
  if (value === undefined) {
    return undefined;
  }
  if (value === null || value.toString() === "") {
    return null;
  }
  return parseInt(value.toString());
};

export const getEligibleAmountFromRate = (rate: RateData): number | null => {
  if (!rate.isGeneratingReceipt || !rate.amount || rate.isPayWhatYouCan) {
    return null;
  }
  const receiptEligibleAmount = rate.receiptEligibleAmount ? parseFloat(rate.receiptEligibleAmount.toString()) : 0;
  return receiptEligibleAmount > rate.amount ? rate.amount : Math.floor(receiptEligibleAmount);
};

export const mapDataToProvideRates = (rates: RateOutput[] | undefined | null, locale: Locales) => {
  if (!rates) {
    return [];
  }
  return rates
    .slice()
    .sort((rateA, rateB) => rateA.sortIndex - rateB.sortIndex)
    .map((rate) => {
      const rateField = rate.rateFields?.find((rateField) => rateField.locale === locale);
      const membershipValidityPeriodAtDate = rate.membershipValidityPeriodAtDate
        ? getDatePlusNHours(rate.membershipValidityPeriodAtDate, 12)
        : undefined;

      const photos = mapDataToProvidePhotoUrls(rate);

      return {
        id: rate.id,
        name: rateField?.title || "",
        description: rateField?.description || "",
        amount: rate.amount,
        receiptEligibleAmount: rate.eligibleAmount || 0,
        seats: rate.seats,
        rateMinimumNumberToBuy: rate.minimumToBuy,
        rateMaximumNumberToBuy: rate.maximumToBuy,
        attendeesByTicket: rate.attendeesByTicket,
        isGroup: Boolean(rate.attendeesByTicket),
        isGroupMembership: Boolean(rate.groupMembershipMaxMembers),
        groupMembershipMaxMembers: rate.groupMembershipMaxMembers,
        isGeneratingReceipt: Boolean(rate.eligibleAmount),
        alreadyBoughtTicketsCount: rate.alreadyBoughtTicketsCount || 0,
        membershipValidityPeriod: (rate.membershipValidityPeriod as MembershipValidityPeriod) || undefined,
        membershipValidityPeriodAtDate,
        photos,
        ...mapApiToScheduleFields(rate),
        bidIncrement: rate.bidIncrement || undefined,
        allowAutomaticRenewal: Boolean(rate.allowAutomaticRenewal),
        isPayWhatYouCan: Boolean(rate.isPayWhatYouCan),
        payWhatYouCanMinimumAmount: rate.payWhatYouCanMinimumAmount ?? null,
        payWhatYouCanSuggestedAmount: rate.payWhatYouCanSuggestedAmount ?? null,
      };
    });
};

export const mapDataToProvidePhotoUrls = (rate: RateOutput): RatePhotoData[] => {
  if (!rate.photoUrls?.length) {
    return [{ photoUrl: null, id: v4() }];
  }
  return rate.photoUrls.map((photoUrl) => ({ photoUrl, id: v4() }));
};

export const mapRateOutputToRateData = (rate: RateOutput, locale: Locales): RateData => {
  const rateField = (rate.rateFields as RateFieldOutput[] | undefined)?.find((field) => field.locale === locale);

  return {
    id: rate.id,
    name: rateField?.title || "",
    description: rateField?.description || "",
    amount: rate.amount,
    attendeesByTicket: rate.attendeesByTicket,
    isGroup: Boolean(rate.attendeesByTicket),
    isGroupMembership: Boolean(rate.groupMembershipMaxMembers),
    groupMembershipMaxMembers: rate.groupMembershipMaxMembers,
    isGeneratingReceipt: Boolean(rate.eligibleAmount),
    receiptEligibleAmount: rate?.eligibleAmount || null,
    seats: rate.seats,
    rateMinimumNumberToBuy: rate.minimumToBuy,
    alreadyBoughtTicketsCount: rate.alreadyBoughtTicketsCount || undefined,
    rateMaximumNumberToBuy: rate.maximumToBuy,
    membershipValidityPeriod: rate.membershipValidityPeriod || null,
    membershipValidityPeriodAtDate: rate.membershipValidityPeriodAtDate
      ? new Date(rate.membershipValidityPeriodAtDate)
      : undefined,
    allowAutomaticRenewal: rate.allowAutomaticRenewal || false,
    ...mapApiToScheduleFields(rate),
    bidIncrement: rate.bidIncrement || undefined,
    photos: rate.photoUrls?.map((photoUrl) => ({ photoUrl, id: crypto.randomUUID() })),
    isPayWhatYouCan: Boolean(rate.isPayWhatYouCan),
    payWhatYouCanMinimumAmount: rate.payWhatYouCanMinimumAmount ?? null,
    payWhatYouCanSuggestedAmount: rate.payWhatYouCanSuggestedAmount ?? null,
    isMemberOnly: rate.isMemberOnly ?? false,
    memberOnlyMembershipRateIds: rate.memberOnlyMembershipRateIds ?? [],
    isHidden: rate.isHidden ?? false,
    shopVariants: mapShopVariantsOutput(rate, locale),
    shopVariantsToDelete: [],
    variantSeats: mapVariantSeatsOutput(rate),
    categoryIds: rate.categoryIds ?? [],
    taxIds: rate.taxIds ?? [],
  };
};

const mapShopVariantsOutput = (rate: RateOutput, locale: Locales): ShopVariantData[] | undefined => {
  const shopVariants = (rate as Record<string, unknown>).shopVariants as
    | Array<{
        id: string;
        sortIndex: number;
        type: ShopVariantType;
        shopVariantFields?: Array<{ locale: string; name: string }>;
        shopVariantValues?: Array<{
          id: string;
          sortIndex: number;
          shopVariantValueFields?: Array<{ locale: string; name: string }>;
        }>;
      }>
    | undefined;

  if (!shopVariants?.length) {
    return undefined;
  }

  return shopVariants.map((variant) => {
    const variantField = variant.shopVariantFields?.find((field) => field.locale === locale);
    const name = variantField?.name || variant.shopVariantFields?.[0]?.name || "";

    const values =
      variant.shopVariantValues?.map((value) => {
        const valueField = value.shopVariantValueFields?.find((field) => field.locale === locale);
        return {
          id: value.id,
          name: valueField?.name || value.shopVariantValueFields?.[0]?.name || "",
          sortIndex: value.sortIndex,
        };
      }) || [];

    return {
      id: variant.id,
      name,
      sortIndex: variant.sortIndex,
      type: variant.type,
      values,
    };
  });
};

const mapVariantSeatsOutput = (rate: RateOutput): VariantSeatData[] | undefined => {
  const variantSeats = (rate as RateOutput & { variantSeats?: VariantSeatData[] }).variantSeats;

  if (!variantSeats?.length) {
    return undefined;
  }

  return variantSeats.map((entry: VariantSeatData) => ({
    combinationKey: entry.combinationKey,
    seats: entry.seats,
    soldCount: entry.soldCount ?? 0,
  }));
};

export const mapRateOutputsToRateData = (rates: RateOutput[], locale: Locales): RateData[] => {
  return rates.map((rate) => mapRateOutputToRateData(rate, locale));
};
